import { formatSlotRange } from "@/lib/datetime";

/** A slot as it appears inside an A2A payload's `raw` JSON. */
export interface WireSlot {
  start: string;
  end: string;
}

interface A2ARaw {
  slots?: WireSlot[];
  slot?: WireSlot;
  reason?: string;
  rationale?: string;
}

function asRaw(raw: unknown): A2ARaw {
  return raw && typeof raw === "object" ? (raw as A2ARaw) : {};
}

/** Slots carried by an A2A payload: the `slots[]` array, or the single `slot`. */
export function slotsFromRaw(raw: unknown): WireSlot[] {
  const r = asRaw(raw);
  if (Array.isArray(r.slots)) return r.slots;
  if (r.slot) return [r.slot];
  return [];
}

export function reasonFromRaw(raw: unknown): string | null {
  const r = asRaw(raw);
  return r.reason ?? r.rationale ?? null;
}

export interface A2aSummary {
  /** Heading for the slot list / note (e.g. "Proposed times"). */
  label: string;
  /** Human-formatted slot ranges. */
  slots: string[];
  /** Free-text note for reject / give_up. */
  note: string | null;
}

/** What the drawer shows for one A2A wire event. Case-insensitive on type. */
export function summarizeA2a(messageType: string, raw: unknown): A2aSummary {
  const slots = slotsFromRaw(raw).map((s) => formatSlotRange(s.start, s.end));
  const reason = reasonFromRaw(raw);
  switch (messageType.toLowerCase()) {
    case "propose_slots":
      return { label: "Offered availability", slots, note: null };
    case "counter_propose":
      return { label: "Countered availability", slots, note: null };
    case "accept":
      return { label: "Agreed on", slots, note: null };
    case "reject":
      return { label: "Rejected", slots: [], note: reason };
    case "give_up":
      return { label: "Gave up", slots: [], note: reason };
    default:
      return { label: messageType, slots, note: reason };
  }
}

/**
 * Closing-divider label for a terminal A2A event ("booked <start–end>" /
 * "no agreement"), or null for intermediate events. Pairs with
 * {@link conclusionFor}: same terminal cases, divider-sized text.
 */
export function outcomeLabelFor(messageType: string, raw: unknown): string | null {
  switch (messageType.toLowerCase()) {
    case "accept": {
      const slot = slotsFromRaw(raw)[0];
      return slot ? `booked ${formatSlotRange(slot.start, slot.end)}` : "booked";
    }
    case "give_up":
    case "reject":
      return "no agreement";
    default:
      return null;
  }
}

/**
 * The closing sentence of a booked outcome. Exported so the chat can render it
 * as a link to the meeting on the calendar instead of plain prose. The bubble
 * splits on this exact string, so it must stay the literal tail of
 * {@link bookedOutcomeText} — hence one constant rather than two copies.
 */
export const CALENDAR_SUFFIX = "It's on your calendar.";

/**
 * Canonical "booked" outcome line, shared by the live narration (ChatPage) and
 * the refresh-hydration path (buildMessages) so a conversation reads IDENTICALLY
 * before and after a reload. {@code incomingWho} is the initiator's label when
 * the signed-in user was the invitee (their agent acted on their behalf); null
 * when they initiated the request. Tense-neutral ("It's on your calendar") so it
 * reads correctly both as live narration and as historical context.
 */
export function bookedOutcomeText(range: string, incomingWho: string | null): string {
  return incomingWho
    ? `Accepted on your behalf — ${range} with ${incomingWho}. ${CALENDAR_SUFFIX}`
    : `Agreement reached — locked in ${range}. ${CALENDAR_SUFFIX}`;
}

/** Canonical "no agreement" line — same live/refresh contract as {@link bookedOutcomeText}. */
export function noAgreementOutcomeText(
  incomingWho: string | null,
  reason: string | null,
): string {
  const tail = reason ? ` (${reason})` : "";
  if (incomingWho) {
    return `Couldn't reach an agreement with ${incomingWho}${tail}.`;
  }
  return `Couldn't reach an agreement${tail}. A few options to try: widen your availability for the next attempt, drop any optional participants, or try again next week.`;
}

/**
 * A main-chat conclusion line when a negotiation reaches a terminal A2A event,
 * or null for intermediate events. Gives every conversation a visible outcome
 * (booked time, or no-agreement) that survives refresh. {@code incomingWho}
 * distinguishes an invitee (agent acted on their behalf) from the initiator so
 * the phrasing matches the live narration exactly.
 */
export function conclusionFor(
  messageType: string,
  raw: unknown,
  incomingWho: string | null,
): string | null {
  switch (messageType.toLowerCase()) {
    case "accept": {
      const slot = slotsFromRaw(raw)[0];
      const range = slot ? formatSlotRange(slot.start, slot.end) : "the agreed time";
      return bookedOutcomeText(range, incomingWho);
    }
    case "give_up":
    case "reject":
      return noAgreementOutcomeText(incomingWho, reasonFromRaw(raw));
    default:
      return null;
  }
}
